/* -------------------------------------------------------
   ChatPage — Main async-chat page.
   Orchestrates the sidebar, message list, composer,
   prompt chips, auto-scroll, and debug toggle.
   ------------------------------------------------------- */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useChatJobs } from '../../hooks/useChatJobs';
import { useConversations } from '../../hooks/useConversations';
import ChatMessageComponent from './ChatMessage';
import ChatComposer from './ChatComposer';
import PromptChips from './PromptChips';
import ConversationSidebar from './ConversationSidebar';
import './ChatPage.css';

interface Props {
  username?: string | null;
}

const ChatPage: React.FC<Props> = ({ username }) => {
  const userId = username || 'demo';
  const isDev =
    process.env.NODE_ENV !== 'production' || userId === 'demo';

  const [debugMode, setDebugMode] = useState(false);
  const [input, setInput] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const {
    messages,
    conversationId,
    isSubmitting,
    sendMessage,
    stopJob,
    retry,
    reset,
    loadConversation,
  } = useChatJobs(userId, debugMode);

  const {
    conversations,
    isLoading: convsLoading,
    activeId,
    setActiveId,
    refresh: refreshConvs,
    load: loadConv,
    rename: renameConv,
    remove: removeConv,
  } = useConversations();

  /* ---- Refresh conversation list when a job completes ---- */
  const completedCount = messages.filter(
    (m) => m.jobStatus === 'COMPLETE',
  ).length;
  const prevCompletedRef = useRef(completedCount);

  useEffect(() => {
    if (completedCount > prevCompletedRef.current) {
      refreshConvs();
    }
    prevCompletedRef.current = completedCount;
  }, [completedCount, refreshConvs]);

  /* ---- Keep sidebar activeId in sync with current conversation ---- */
  useEffect(() => {
    if (conversationId) {
      setActiveId(conversationId);
    }
  }, [conversationId, setActiveId]);

  /* ---- auto-scroll (respects user scroll position) ---- */
  const scrollRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const nearBottom = useRef(true);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    nearBottom.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);

  useEffect(() => {
    if (nearBottom.current) {
      anchorRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  /* ---- scroll-to-bottom helper ---- */
  const scrollDown = useCallback(() => {
    nearBottom.current = true;
    setTimeout(
      () => anchorRef.current?.scrollIntoView({ behavior: 'smooth' }),
      60,
    );
  }, []);

  /* ---- handlers ---- */
  const doSend = useCallback(() => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    sendMessage(text);
    scrollDown();
  }, [input, sendMessage, scrollDown]);

  const handleChip = useCallback(
    (text: string) => {
      sendMessage(text);
      scrollDown();
    },
    [sendMessage, scrollDown],
  );

  const handleFollowup = useCallback(
    (text: string) => {
      sendMessage(text);
      scrollDown();
    },
    [sendMessage, scrollDown],
  );

  const handleNewChat = useCallback(() => {
    reset();
    setActiveId(null);
  }, [reset, setActiveId]);

  const handleSelectConversation = useCallback(
    async (id: string) => {
      const detail = await loadConv(id);
      if (detail) {
        loadConversation(detail);
        // Scroll to bottom after loading
        setTimeout(() => {
          nearBottom.current = true;
          anchorRef.current?.scrollIntoView({ behavior: 'auto' });
        }, 80);
      }
    },
    [loadConv, loadConversation],
  );

  const userInitial = (username || 'U').charAt(0).toUpperCase();
  const isEmpty = messages.length === 0;

  return (
    <div className="cv2-page">
      {/* ---- Sidebar ---- */}
      <ConversationSidebar
        conversations={conversations}
        activeId={activeId}
        isLoading={convsLoading}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onSelect={handleSelectConversation}
        onNewChat={handleNewChat}
        onRename={renameConv}
        onDelete={removeConv}
      />

      {/* ---- Main content area ---- */}
      <div className="cv2-main">
        {/* ---- Top bar ---- */}
        <div className="cv2-topbar">
          <div className="cv2-topbar-inner">
            <div className="cv2-topbar-left">
              <button
                className="cv2-topbar-hamburger"
                onClick={() => setSidebarOpen((v) => !v)}
                aria-label="Toggle conversation history"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
              <div className="cv2-topbar-title">
                <span className="cv2-topbar-brand">ProfiTree AI</span>
                <span className="cv2-topbar-badge">Beta</span>
              </div>
            </div>
            <div className="cv2-topbar-actions">
              {isDev && (
                <label
                  className="cv2-debug-toggle"
                  title="Toggle debug mode"
                >
                  <input
                    type="checkbox"
                    checked={debugMode}
                    onChange={(e) => setDebugMode(e.target.checked)}
                  />
                  <span>Debug</span>
                </label>
              )}
              {messages.length > 0 && (
                <button
                  className="cv2-new-chat-btn"
                  onClick={handleNewChat}
                  aria-label="Start a new conversation"
                >
                  + New Chat
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ---- Scrollable message area ---- */}
        <div
          className="cv2-scroll-area"
          ref={scrollRef}
          onScroll={handleScroll}
        >
          <div className="cv2-messages">
            {/* Empty state */}
            {isEmpty && (
              <div className="cv2-welcome">
                <div className="cv2-welcome-icon" aria-hidden="true">
                  <svg
                    width="44"
                    height="44"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#2A9D8F"
                    strokeWidth="1.5"
                  >
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                  </svg>
                </div>
                <h1 className="cv2-welcome-title">
                  How can I help you today?
                </h1>
                <p className="cv2-welcome-sub">
                  Ask about your tax situation, portfolio analysis.
                  <br />
                  <span className="cv2-welcome-comfort">
                    No jargon, no judgment. Ask in your own words; we’ll keep it clear.
                  </span>
                </p>
                <PromptChips onSelect={handleChip} />
              </div>
            )}

            {/* Message list */}
            {messages.map((msg) => (
              <ChatMessageComponent
                key={msg.id}
                message={msg}
                showDebug={debugMode}
                onFollowup={handleFollowup}
                onStop={stopJob}
                onRetry={retry}
                userInitial={userInitial}
              />
            ))}

            <div ref={anchorRef} className="cv2-scroll-anchor" />
          </div>
        </div>

        {/* ---- Sticky composer ---- */}
        <ChatComposer
          value={input}
          onChange={setInput}
          onSend={doSend}
          disabled={isSubmitting}
        />
      </div>

      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default ChatPage;
